"use client";

import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  PromptHistoryDialog,
  type PromptVersionEntry,
} from "@/components/agents/shared/prompt-history-dialog";
import {
  useToolInstructionVersionsQuery,
  type ToolInstructionScopeType,
} from "@/hooks/queries/tool-instruction-versions";
import { Badge } from "@/components/ui/badge";
import { Loader2, RotateCcw, Save, AlertCircle, History } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl"



interface ToolInstructionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  toolName: string;
  toolDisplayName: string;
  /** Scope type for per-agent instructions (e.g., 'email_agent', 'voice_agent') */
  scopeType?: string;
  /** Scope ID (e.g., agent UUID) */
  scopeId?: string;
  /** Callback when instructions are saved successfully */
  onSave?: () => void;
  /** Callback when instructions are reset to default */
  onReset?: () => void;
}

export function ToolInstructionsDialog({
  open,
  onOpenChange,
  toolName,
  toolDisplayName,
  scopeType,
  scopeId,
  onSave,
  onReset,
}: ToolInstructionsDialogProps) {

  const tUi = useTranslations("AgentTools.AgentsSharedDialogsToolInstructionsDialog")
  const tCommonUi = useTranslations("Common")

  const { fetchWithAuth } = useAuth();

  const [instructions, setInstructions] = useState("");
  const [defaultInstructions, setDefaultInstructions] = useState("");
  const [source, setSource] = useState<"tenant" | "scoped" | "default">("default");
  const [originalInstructions, setOriginalInstructions] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // A tenant-wide default and a per-agent override are separate histories for the same tool.
  // The prop is a loose `string` shared with several callers; the server action's zod enum is
  // the real gate, so an unexpected value fails there rather than reading someone else's list.
  const versionsQuery = useToolInstructionVersionsQuery(
    toolName,
    (scopeType as ToolInstructionScopeType | undefined) ?? null,
    scopeId ?? null,
    showHistory,
  );

  const historyEntries: PromptVersionEntry[] = (versionsQuery.data ?? []).map((version) => ({
    id: version.id,
    text: version.system_instructions,
    createdAt: version.created_at,
  }));

  const hasChanges = instructions !== originalInstructions;
  const isUsingCustom = source === "tenant" || source === "scoped";

  // Load instructions when dialog opens
  const loadInstructions = useCallback(async () => {
    if (!open) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams({ tool_name: toolName });
      if (scopeType && scopeId) {
        params.set('scope_type', scopeType);
        params.set('scope_id', scopeId);
      }
      const res = await fetchWithAuth(
        `/api/ai/tools/instructions?${params}`
      );
      if (res.ok) {
        const data = await res.json();
        setInstructions(data.system_instructions || "");
        setOriginalInstructions(data.system_instructions || "");
        setDefaultInstructions(data.default_instructions || "");
        setSource(data.source);
      } else {
        toast.error(tUi("failedToLoadToolInstructions"));
      }
    } catch (error) {
      console.error("Error loading instructions:", error);
      toast.error(tUi("failedToLoadToolInstructions"));
    } finally {
      setIsLoading(false);
    }
  }, [open, toolName, scopeType, scopeId, fetchWithAuth, tUi]);

  useEffect(() => {
    loadInstructions();
  }, [loadInstructions]);

  // Save custom instructions
  const handleSave = async () => {
    if (!instructions.trim()) {
      toast.error(tUi("instructionsCannotBeEmpty"));
      return;
    }

    setIsSaving(true);
    try {
      const res = await fetchWithAuth("/api/ai/tools/instructions", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tool_name: toolName,
          system_instructions: instructions,
          scope_type: scopeType || null,
          scope_id: scopeId || null,
        }),
      });

      if (res.ok) {
        setOriginalInstructions(instructions);
        setSource(scopeType && scopeId ? "scoped" : "tenant");
        toast.success(tUi("instructionsSavedSuccessfully"));
        onSave?.();
      } else {
        const data = await res.json();
        toast.error(data.error || "Failed to save instructions");
      }
    } catch (error) {
      console.error("Error saving instructions:", error);
      toast.error(tUi("failedToSaveInstructions"));
    } finally {
      setIsSaving(false);
    }
  };

  // Reset to default (delete tenant/scoped override)
  const handleResetToDefault = async () => {
    setIsResetting(true);
    try {
      const params = new URLSearchParams({ tool_name: toolName });
      if (scopeType && scopeId) {
        params.set('scope_type', scopeType);
        params.set('scope_id', scopeId);
      }
      const res = await fetchWithAuth(
        `/api/ai/tools/instructions?${params}`,
        { method: "DELETE" }
      );

      if (res.ok) {
        setInstructions(defaultInstructions);
        setOriginalInstructions(defaultInstructions);
        setSource("default");
        setShowResetConfirm(false);
        toast.success(tUi("instructionsResetToDefault"));
        onReset?.();
      } else {
        const data = await res.json();
        toast.error(data.error || "Failed to reset instructions");
      }
    } catch (error) {
      console.error("Error resetting instructions:", error);
      toast.error(tUi("failedToResetInstructions"));
    } finally {
      setIsResetting(false);
    }
  };

  // Discard changes (revert to original)
  const handleDiscardChanges = () => {
    setInstructions(originalInstructions);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {toolDisplayName} <span><span key={tUi("instructions")}>{tUi("instructions")}</span></span>
            <Badge
              variant="outline"
              className={cn(
                "text-xs ml-2",
                isUsingCustom
                  ? "bg-green-50 text-green-700 border-green-200"
                  : "bg-gray-50 text-gray-700 border-gray-200"
              )}
            >
              <span key={source === "scoped" ? tUi("agentCustom") : source === "tenant" ? tUi("tenantCustom") : tUi("default")}>{source === "scoped" ? tUi("agentCustom") : source === "tenant" ? tUi("tenantCustom") : tUi("default")}</span>
            </Badge>
          </DialogTitle>
          <DialogDescription className="space-y-2">
            <span>
              <span key={tUi("configureHowAIShouldUseThisToolCustom")}>{tUi("configureHowAIShouldUseThisToolCustom")}</span></span>
            <span className="flex items-center gap-2 pt-1">
              <span className="text-xs text-muted-foreground"><span key={tUi("toolName")}>{tUi("toolName")}</span></span>
              <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">
                {toolName}
              </code>
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden py-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500"><span key={tUi("loadingInstructions")}>{tUi("loadingInstructions")}</span></span>
            </div>
          ) : (
            <div className="space-y-4">
              <Textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder={tUi("enterToolInstructions")}
                className="min-h-[400px] font-mono text-sm resize-none"
              />

              {hasChanges && (
                <div className="flex items-center gap-2 text-sm text-amber-600">
                  <AlertCircle className="w-4 h-4" />
                  <span><span key={tUi("youHaveUnsavedChanges")}>{tUi("youHaveUnsavedChanges")}</span></span>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 gap-2 sm:gap-2">
          <div className="flex gap-2 mr-auto">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setShowHistory(true)}
              disabled={isLoading || isSaving || isResetting}
            >
              <History className="w-3.5 h-3.5" />
              <span><span key={tUi("history")}>{tUi("history")}</span></span>
            </Button>
            {hasChanges && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDiscardChanges}
                disabled={isSaving || isResetting}
              >
                <span key={tUi("discardChanges")}>{tUi("discardChanges")}</span></Button>
            )}
            {isUsingCustom && !hasChanges && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowResetConfirm(true)}
                disabled={isSaving || isResetting || isLoading}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                {isResetting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4 mr-2" />
                )}
                <span><span key={tUi("resetToDefault")}>{tUi("resetToDefault")}</span></span>
              </Button>
            )}
          </div>

          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving || isResetting || isLoading}
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            <span><span key={tUi("save")}>{tUi("save")}</span></span>
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><span key={tUi("resetToDefaultInstructions")}>{tUi("resetToDefaultInstructions")}</span></AlertDialogTitle>
            <AlertDialogDescription>
              <span><span key={tUi("thisWillPermanentlyDeleteYourCustomInstructionsFor")}>{tUi("thisWillPermanentlyDeleteYourCustomInstructionsFor")}</span></span>{" "}
              <code className="px-1 py-0.5 bg-muted rounded text-xs font-mono">
                {toolName}
              </code>{" "}
              <span><span key={tUi("andRevertToTheSystemDefaultThisAction")}>{tUi("andRevertToTheSystemDefaultThisAction")}</span></span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetToDefault}
              disabled={isResetting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isResetting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <span key={tUi("resetting")}>{tUi("resetting")}</span></>
              ) : (
                tUi("resetToDefault")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PromptHistoryDialog
        open={showHistory}
        onOpenChange={setShowHistory}
        entries={historyEntries}
        isLoading={versionsQuery.isLoading}
        error={versionsQuery.isError ? versionsQuery.error : null}
        currentText={instructions}
        onRestore={setInstructions}
        canRestore
      />
    </Dialog>
  );
}
